import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "demo";

const readInitialDemo = (): boolean => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;
  try {
    return JSON.parse(stored) === true;
  } catch {
    return false;
  }
};

type DemoContextValue = {
  isDemo: boolean;
  toggleDemo: () => void;
};

const DemoContext = createContext<DemoContextValue | null>(null);

export const DemoProvider = ({
  children,
  initialValue,
}: PropsWithChildren<{ initialValue?: boolean }>) => {
  const [isDemo, setIsDemo] = useState<boolean>(
    initialValue ?? readInitialDemo,
  );

  const toggleDemo = useCallback(() => {
    setIsDemo((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const value = useMemo(() => ({ isDemo, toggleDemo }), [isDemo, toggleDemo]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export const useDemo = (): boolean => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx.isDemo;
};

export const useToggleDemo = (): (() => void) => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useToggleDemo must be used within DemoProvider");
  return ctx.toggleDemo;
};
